import { Regexes } from "../../../util/Default";
import {
  RBPayBusinessType,
  RBPayBusinessTypeValue,
  RBPayDirectDepositSettings,
  RBPayEmploymentType,
  RBPayFieldErrors,
  RBPayPaymentMethod,
  RBPayPaymentSectionDraft,
  RBPayPaymentSectionErrors,
  RBPayProfileSectionDraft,
  RBPayProfileSectionErrors,
  RBPaySettingsState,
} from "../types/rbPaySettings.types";
import { normalizeBusinessNumber, normalizeDigits } from "../utils/rbPaySettings.formatters";

export const isValidSin = (value: string = "") => normalizeDigits(value).length === 9;

// ---------------------------------------------------------------------------
// Date of Birth — "YYYY-MM-DD", employee profiles only.
//
// Deliberately one notch STRICTER than the backend: its IsPastDate validator uses
// `!isAfter(now, 'day')` in UTC, so it accepts today (and, from a negative UTC
// offset, tomorrow). The spec says today is not allowed, and a DOB of "today" is
// nonsense data, so we reject it here. Being stricter than the backend can never
// produce a false 422.
//
// Every comparison is on the plain string against a LOCALLY formatted "today" —
// no Date arithmetic on the value itself, so there is no timezone drift.
// ---------------------------------------------------------------------------
const DATE_OF_BIRTH_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const MINIMUM_DATE_OF_BIRTH = "1900-01-01";

const toLocalDateString = (date: Date) =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;

export const getTodayAsDateString = () => toLocalDateString(new Date());

// Upper bound for the date input's calendar: today is not a valid birth date, so
// the newest selectable day is yesterday. Same rule the validator enforces, applied
// at the picker so an invalid date can't be chosen in the first place.
export const getMaxDateOfBirthString = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return toLocalDateString(yesterday);
};

// A real day on the calendar: rejects 2023-02-30 / 2023-13-01, accepts 2024-02-29.
// Constructed with the local-time constructor and compared component-wise, since
// JS silently rolls overflowing dates forward (Feb 30 -> Mar 2).
export const isRealCalendarDate = (value: string = "") => {
  if (!DATE_OF_BIRTH_PATTERN.test(value)) {
    return false;
  }

  const [year, month, day] = value.split("-").map(Number);
  const parsedDate = new Date(year, month - 1, day);

  return (
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getDate() === day
  );
};

export const isValidDateOfBirth = (value: string = "") =>
  isRealCalendarDate(value) &&
  value < getTodayAsDateString() &&
  value >= MINIMUM_DATE_OF_BIRTH;

// Single source of the DOB message for every caller (both validate entry points
// and the pharma path), so the wording can never drift between them.
export const getDateOfBirthError = (value: string = "") => {
  const dateOfBirth = String(value || "").trim();

  if (!dateOfBirth) {
    return "Date of Birth is required.";
  }

  if (!isRealCalendarDate(dateOfBirth)) {
    return "Enter a valid date of birth in YYYY-MM-DD format.";
  }

  if (dateOfBirth >= getTodayAsDateString()) {
    return "Date of Birth must be a date in the past.";
  }

  if (dateOfBirth < MINIMUM_DATE_OF_BIRTH) {
    return "Enter a valid date of birth.";
  }

  return "";
};

export const isValidBusinessNumber = (value: string = "") =>
  /^(\d{9}|\d{9}[A-Z]{2}\d{4})$/.test(normalizeBusinessNumber(value));

// Requirement: Sole Proprietor must use the GST/HST "RT" suffix, Incorporated
// must use the Corporation "RC" suffix. e.g. 123456789RT0001 / 123456789RC0001
export const getBusinessNumberSuffixForType = (businessType?: RBPayBusinessTypeValue | null) =>
  businessType === RBPayBusinessType.INCORPORATED ? "RC" : "RT";

export const isValidBusinessNumberForType = (
  value: string = "",
  businessType?: RBPayBusinessTypeValue | null
) => {
  const letters = getBusinessNumberSuffixForType(businessType);
  return new RegExp(`^\\d{9}${letters}\\d{4}$`).test(normalizeBusinessNumber(value));
};

// "Remove my Direct Deposit details" sentinel: EVERY banking field is filled and
// all-zero (enter zeros in institution, transit and account, then Save). Leaving
// fields EMPTY is NOT a removal — empty is treated as incomplete so it produces
// "required" errors and never marks the section/checklist complete. To clear
// saved banking details the user must explicitly enter zeros in every field.
export const isDirectDepositCleared = (directDeposit: RBPayDirectDepositSettings) => {
  const institutionNumber = normalizeDigits(directDeposit.institutionNumber);
  const transitNumber = normalizeDigits(directDeposit.transitNumber);
  const accountNumber = normalizeDigits(directDeposit.accountNumber);
  const allFieldsFilled =
    institutionNumber.length > 0 && transitNumber.length > 0 && accountNumber.length > 0;
  return allFieldsFilled && /^0+$/.test(institutionNumber + transitNumber + accountNumber);
};

export const validateRBPaySettings = (state: RBPaySettingsState): RBPayFieldErrors => {
  const errors: RBPayFieldErrors = {};

  if (!state.paymentMethod) {
    errors.paymentMethod = "Preferred payment method is required.";
  }

  // Date of Birth applies to BOTH industries, unlike the identity fields below:
  // the backend requires it purely on `payrollProfileType === employee` with no
  // industry check, and pharma derives `employee` from the account — so a pharma
  // worker without a DOB is a 422 at profile creation.
  if (state.employmentType === RBPayEmploymentType.EMPLOYEE) {
    const dateOfBirthError = getDateOfBirthError(state.employeeProfile.dateOfBirth);

    if (dateOfBirthError) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        dateOfBirth: dateOfBirthError,
      };
    }
  }

  // Pharma workers don't collect or send identity fields from RB Pay — SIN,
  // business name/number and tax profile are managed on the SIN/Business
  // Information page and persisted on the account. Validating them here would
  // flag "required" errors for fields the pharma flow never shows; Save is gated
  // separately by the pharma profile-missing check.
  if (state.industryMode !== "Pharmaceutical" && state.employmentType === RBPayEmploymentType.EMPLOYEE) {
    if (!state.employeeProfile.sinNumber) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        sinNumber: "SIN is required",
      };
    } else if (!isValidSin(state.employeeProfile.sinNumber)) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        sinNumber: "SIN must be 9 digits",
      };
    }

    if (!state.employeeProfile.taxProfile) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        taxProfile: "Tax Profile is required.",
      };
    }
  }

  if (state.industryMode !== "Pharmaceutical" && state.employmentType === RBPayEmploymentType.BUSINESS) {
    if (!state.businessProfile.businessType) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        businessType: "Business Type is required.",
      };
    }

    if (!state.businessProfile.legalBusinessName.trim()) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        legalBusinessName: "Legal Business Name is required.",
      };
    }

    if (!state.businessProfile.businessNumber) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        businessNumber: "Business number is required",
      };
    } else if (
      !isValidBusinessNumberForType(
        state.businessProfile.businessNumber,
        state.businessProfile.businessType
      )
    ) {
      const suffix = getBusinessNumberSuffixForType(state.businessProfile.businessType);
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        businessNumber: `Business number must be in the format 123-456-789 ${suffix}0001`,
      };
    }

    if (!state.businessProfile.certificationAccepted) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        certificationAccepted: "Certification is required before saving a business profile.",
      };
    }
  }

  if (
    state.paymentMethod === RBPayPaymentMethod.DIRECT_DEPOSIT &&
    !isDirectDepositCleared(state.directDeposit)
  ) {
    const institutionNumber = normalizeDigits(state.directDeposit.institutionNumber);
    const transitNumber = normalizeDigits(state.directDeposit.transitNumber);
    const accountNumber = normalizeDigits(state.directDeposit.accountNumber);

    if (!institutionNumber) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        institutionNumber: "Institution number is required",
      };
    } else if (institutionNumber.length !== 3) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        institutionNumber: "Institution number must be exactly 3 digits",
      };
    }

    if (!transitNumber) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        transitNumber: "Transit number is required",
      };
    } else if (transitNumber.length !== 5) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        transitNumber: "Transit number must be exactly 5 digits",
      };
    }

    if (!accountNumber) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        accountNumber: "Account number is required",
      };
    } else if (accountNumber.length < 7 || accountNumber.length > 12) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        accountNumber: "Account number must be between 7 and 12 digits",
      };
    }

    if (!state.directDeposit.document) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        document: "A banking document is required.",
      };
    }

    if (!state.directDeposit.authorizationAccepted) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        authorizationAccepted: "Authorization is required before saving direct deposit information.",
      };
    }
  }

  if (state.paymentMethod === RBPayPaymentMethod.EMAIL_TRANSFER) {
    const email = String(state.emailTransfer.email || "").trim();

    if (!email) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        email: "Email address is required",
      };
    } else if (!Regexes.emailPattern.test(email)) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        email: "Must be a valid email address",
      };
    } else if (!state.emailTransfer.emailVerified) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        emailVerified: "Please verify this email address before saving.",
      };
    }

    if (!state.emailTransfer.certificationAccepted) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        certificationAccepted: "Certification is required before saving email money transfer settings.",
      };
    }
  }

  return errors;
};

export const validateRBPayProfileSection = (
  state: RBPayProfileSectionDraft
): RBPayProfileSectionErrors => {
  const errors: RBPayProfileSectionErrors = {};

  if (state.industryMode !== "Pharmaceutical" && !state.employmentType) {
    errors.employmentType = "Preferred payment profile type is required.";
  }

  // Both industries — see the matching block in validateRBPaySettings.
  if (state.employmentType === RBPayEmploymentType.EMPLOYEE) {
    const dateOfBirthError = getDateOfBirthError(state.employeeProfile.dateOfBirth);

    if (dateOfBirthError) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        dateOfBirth: dateOfBirthError,
      };
    }
  }

  // Pharma workers don't collect or send identity fields from RB Pay — SIN,
  // business name/number and tax profile are managed on the SIN/Business
  // Information page and persisted on the account. Validating them here would
  // flag "required" errors for fields the pharma flow never shows; Save is gated
  // separately by the pharma profile-missing check.
  if (state.industryMode !== "Pharmaceutical" && state.employmentType === RBPayEmploymentType.EMPLOYEE) {
    if (!state.employeeProfile.sinNumber) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        sinNumber: "SIN is required",
      };
    } else if (!isValidSin(state.employeeProfile.sinNumber)) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        sinNumber: "SIN must be 9 digits",
      };
    }

    if (!state.employeeProfile.taxProfile) {
      errors.employeeProfile = {
        ...(errors.employeeProfile || {}),
        taxProfile: "Tax Profile is required.",
      };
    }
  }

  if (state.industryMode !== "Pharmaceutical" && state.employmentType === RBPayEmploymentType.BUSINESS) {
    if (!state.businessProfile.businessType) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        businessType: "Business Type is required.",
      };
    }

    if (!state.businessProfile.legalBusinessName.trim()) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        legalBusinessName: "Legal Business Name is required.",
      };
    }

    if (!state.businessProfile.businessNumber) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        businessNumber: "Business number is required",
      };
    } else if (
      !isValidBusinessNumberForType(
        state.businessProfile.businessNumber,
        state.businessProfile.businessType
      )
    ) {
      const suffix = getBusinessNumberSuffixForType(state.businessProfile.businessType);
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        businessNumber: `Business number must be in the format 123-456-789 ${suffix}0001`,
      };
    }

    if (!state.businessProfile.certificationAccepted) {
      errors.businessProfile = {
        ...(errors.businessProfile || {}),
        certificationAccepted: "Certification is required before saving a business profile.",
      };
    }
  }

  return errors;
};

export const validateRBPayPaymentSection = (
  state: RBPayPaymentSectionDraft
): RBPayPaymentSectionErrors => {
  const errors: RBPayPaymentSectionErrors = {};

  if (!state.paymentMethod) {
    errors.paymentMethod = "Preferred payment method is required.";
  }

  if (
    state.paymentMethod === RBPayPaymentMethod.DIRECT_DEPOSIT &&
    !isDirectDepositCleared(state.directDeposit)
  ) {
    const institutionNumber = normalizeDigits(state.directDeposit.institutionNumber);
    const transitNumber = normalizeDigits(state.directDeposit.transitNumber);
    const accountNumber = normalizeDigits(state.directDeposit.accountNumber);

    if (!institutionNumber) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        institutionNumber: "Institution number is required",
      };
    } else if (institutionNumber.length !== 3) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        institutionNumber: "Institution number must be exactly 3 digits",
      };
    }

    if (!transitNumber) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        transitNumber: "Transit number is required",
      };
    } else if (transitNumber.length !== 5) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        transitNumber: "Transit number must be exactly 5 digits",
      };
    }

    if (!accountNumber) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        accountNumber: "Account number is required",
      };
    } else if (accountNumber.length < 7 || accountNumber.length > 12) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        accountNumber: "Account number must be between 7 and 12 digits",
      };
    }

    if (!state.directDeposit.document) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        document: "A banking document is required.",
      };
    }

    if (!state.directDeposit.authorizationAccepted) {
      errors.directDeposit = {
        ...(errors.directDeposit || {}),
        authorizationAccepted: "Authorization is required before saving direct deposit information.",
      };
    }
  }

  if (state.paymentMethod === RBPayPaymentMethod.EMAIL_TRANSFER) {
    const email = String(state.emailTransfer.email || "").trim();

    if (!email) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        email: "Email address is required",
      };
    } else if (!Regexes.emailPattern.test(email)) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        email: "Must be a valid email address",
      };
    } else if (!state.emailTransfer.emailVerified) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        emailVerified: "Please verify this email address before saving.",
      };
    }

    if (!state.emailTransfer.certificationAccepted) {
      errors.emailTransfer = {
        ...(errors.emailTransfer || {}),
        certificationAccepted: "Certification is required before saving email money transfer settings.",
      };
    }
  }

  return errors;
};

export const hasValidationErrors = (errors: RBPayFieldErrors) =>
  JSON.stringify(errors) !== JSON.stringify({});
